import { useEffect, useRef, useState } from "react";

/** Numbered marker shown next to a detected field on the document. */
export interface FieldMarker {
  index: number;
  fieldName: string;
  /** Bbox of the field's KEY (label) in natural image coords. */
  keyBbox: { xmin: number; ymin: number; xmax: number; ymax: number };
}

interface DocumentImageWithHighlightProps {
  src: string;
  alt: string;
  /** "x,y,width,height" in the natural image coordinate space. Empty → no highlight. */
  highlightBoundary?: string;
  /** Image natural size hint as "W,H" (from `image_size` in select_projection). */
  naturalSizeHint?: string;
  /** Zoom 50–200, default 100. */
  zoom?: number;
  /**
   * Render-prop receiving (anchor, naturalDims) when there's an active
   * highlight. Used to position the FieldEditPopover next to the bbox.
   */
  renderOverlay?: (
    anchor: { x: number; y: number; width: number; height: number },
    natural: { width: number; height: number },
  ) => React.ReactNode;
  /** Called once the image is loaded, exposing the underlying element. */
  onImageRef?: (el: HTMLImageElement | null) => void;
  /** When true, scrolls the bbox into view as the active highlight changes. */
  autoScrollToHighlight?: boolean;
  /** Numbered field markers from select_coordinates. */
  markers?: FieldMarker[];
  /** Field name whose marker should be drawn in the active style. */
  activeMarkerFieldName?: string | null;
  /** Click handler on a marker — typically syncs the side panel selection. */
  onMarkerClick?: (marker: FieldMarker) => void;
  /**
   * When true, suppress all overlays (numbered markers, active bbox,
   * popover) and render just the raw page image. Used by the cockpit's
   * "Original" view-mode toggle.
   */
  cleanView?: boolean;
  /** Red table overlay for Line Item Rule mode. */
  tableOverlay?: {
    x: number;
    y: number;
    width: number;
    height: number;
    rowHeights: number[];
    colWidths: number[];
  } | null;
  onTableOverlayChange?: (overlay: {
    x: number;
    y: number;
    width: number;
    height: number;
    rowHeights: number[];
    colWidths: number[];
  }) => void;
  headerMappings?: Record<string, string>;
  activeHeaderIndex?: { rowIndex: number; colIndex: number } | null;
  onHeaderClick?: (rowIndex: number, colIndex: number) => void;
  renderTableOverlay?: (
    table: { x: number; y: number; width: number; height: number; rowHeights: number[]; colWidths: number[] },
    activeCell: { rowIndex: number; colIndex: number } | null,
    natural: { width: number; height: number },
  ) => React.ReactNode;
  onImageDimensions?: (natural: { width: number; height: number }) => void;
  // Field Rule drawing props
  isRuleMode?: boolean;
  /** True ONLY when activeTab is fielditem AND mode is rule. Used to enable drawing field boundaries. */
  isFieldItemRuleMode?: boolean;
  fieldRulePairs?: Array<{
    whole: { x: number; y: number; w: number; h: number };
    key?: { x: number; y: number; w: number; h: number };
    value?: { x: number; y: number; w: number; h: number };
    field: string;
  }>;
  activeFieldPairIndex?: number | null;
  drawingSelection?: {
    start: { x: number; y: number } | null;
    end: { x: number; y: number } | null;
  };
  onDrawingStart?: (x: number, y: number) => void;
  onDrawingMove?: (x: number, y: number) => void;
  onDrawingEnd?: (endX: number, endY: number) => void;
  onFieldRuleChange?: (index: number, pair: {
    whole: { x: number; y: number; w: number; h: number };
    key?: { x: number; y: number; w: number; h: number };
    value?: { x: number; y: number; w: number; h: number };
    field: string;
  }) => void;
  onFieldRuleSelect?: (index: number) => void;
  onTableClick?: () => void;
  pendingFieldPair?: Partial<{
    whole: { x: number; y: number; w: number; h: number };
    key?: { x: number; y: number; w: number; h: number };
    value?: { x: number; y: number; w: number; h: number };
    field: string;
  }> | null;
  ocrWords?: Array<{
    text: string;
    x: number;
    y: number;
    w: number;
    h: number;
  }>;
  onWordClick?: (text: string) => void;
  /**
   * When true, drops the default `max-w-3xl` cap on the image so it
   * scales to the parent container width instead of overflowing on
   * narrow viewports. Used by the Final Checker where the document
   * pane should never produce a horizontal scrollbar.
   */
  fitWidth?: boolean;
}

interface ImageDims {
  natural: { width: number; height: number };
  rendered: { width: number; height: number };
}

/**
 * Renders the document image with an absolute-positioned SVG overlay that
 * highlights the active bounding box. Boundary coordinates come from the
 * backend in natural-image space (the `image_size` hint, e.g. "2339,1654").
 * We scale them to the rendered size on each layout pass.
 */
export function DocumentImageWithHighlight({
  src,
  alt,
  highlightBoundary,
  naturalSizeHint,
  zoom = 100,
  renderOverlay,
  onImageRef,
  autoScrollToHighlight = true,
  markers,
  activeMarkerFieldName,
  onMarkerClick,
  cleanView = false,
  tableOverlay,
  onTableOverlayChange,
  headerMappings = {},
  activeHeaderIndex,
  onHeaderClick,
  renderTableOverlay,
  onImageDimensions,
  isRuleMode,
  isFieldItemRuleMode,
  fieldRulePairs = [],
  activeFieldPairIndex,
  drawingSelection,
  onDrawingStart,
  onDrawingMove,
  onDrawingEnd,
  onFieldRuleChange,
  onFieldRuleSelect,
  onTableClick,
  pendingFieldPair,
  ocrWords,
  onWordClick,
  fitWidth = false,
}: DocumentImageWithHighlightProps) {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [dims, setDims] = useState<ImageDims | null>(null);
  const isDrawingRef = useRef(false);

  // Track when drawing starts/ends
  useEffect(() => {
    isDrawingRef.current = !!drawingSelection?.start;
  }, [drawingSelection?.start]);

  // Handle field item drawing with mousemove and mouseup
  useEffect(() => {
    if (!isFieldItemRuleMode || !dims) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDrawingRef.current || !imgRef.current || !dims) return;

      const rect = imgRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) * (dims.natural.width / rect.width);
      const y = (e.clientY - rect.top) * (dims.natural.height / rect.height);

      console.log("[handleMouseMove] isDrawing:", isDrawingRef.current, "pos:", { x, y });
      onDrawingMove?.(x, y);
    };

    const handleMouseUp = (e: MouseEvent) => {
      console.log("[handleMouseUp] isDrawing:", isDrawingRef.current);
      if (!isDrawingRef.current || !imgRef.current) {
        return;
      }

      // Capture the final position at the moment of mouseup
      const rect = imgRef.current.getBoundingClientRect();
      const endX = (e.clientX - rect.left) * (dims.natural.width / rect.width);
      const endY = (e.clientY - rect.top) * (dims.natural.height / rect.height);

      console.log("[handleMouseUp] Final position:", { endX, endY });

      // Reset drawing flag BEFORE calling onDrawingEnd to prevent further mousemove events
      isDrawingRef.current = false;

      // Pass final coordinates directly to avoid timing issues
      onDrawingEnd?.(endX, endY);
    };

    document.addEventListener("mousemove", handleMouseMove, true);
    document.addEventListener("mouseup", handleMouseUp, true);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove, true);
      document.removeEventListener("mouseup", handleMouseUp, true);
    };
  }, [isFieldItemRuleMode, dims, onDrawingMove, onDrawingEnd]);

  const handleImageMouseDown = (e: React.MouseEvent) => {
    // Only start field item drawing when specifically in field item rule mode
    if (!isFieldItemRuleMode || !dims) return;
    if ((e.target as HTMLElement).closest('[data-interaction="true"]')) return;

    // Don't start a new drawing if one is already in progress
    if (isDrawingRef.current) {
      console.log("[handleImageMouseDown] Drawing already in progress, ignoring");
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (dims.natural.width / rect.width);
    const y = (e.clientY - rect.top) * (dims.natural.height / rect.height);

    console.log("[handleImageMouseDown] Starting drawing at:", { x, y });

    // Set the isDrawingRef FIRST before calling onDrawingStart
    isDrawingRef.current = true;

    // Start drawing by setting start point
    onDrawingStart?.(x, y);
  };

  const [dragState, setDragState] = useState<{
    type: "move" | "resize-row" | "resize-col" | "resize-corner" | "field-rule-move" | "field-rule-resize";
    index?: number;
    subType?: "whole" | "key" | "value";
    corner?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
    startX: number;
    startY: number;
    totalDist: number;
    initialOverlay?: {
      x: number;
      y: number;
      width: number;
      height: number;
      rowHeights: number[];
      colWidths: number[];
    };
    initialPair?: {
      whole: { x: number; y: number; w: number; h: number };
      key?: { x: number; y: number; w: number; h: number };
      value?: { x: number; y: number; w: number; h: number };
      field: string;
    };
  } | null>(null);

  const handleMouseDown = (
    e: React.MouseEvent,
    type: "move" | "resize-row" | "resize-col" | "resize-corner" | "field-rule-move" | "field-rule-resize",
    index?: number,
    corner?: "top-left" | "top-right" | "bottom-left" | "bottom-right",
    subType?: "whole" | "key" | "value",
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (!dims) return;

    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) * (dims.natural.width / rect.width);
    const y = (e.clientY - rect.top) * (dims.natural.height / rect.height);

    const baseState = {
      type,
      index,
      corner,
      subType,
      startX: x,
      startY: y,
      totalDist: 0,
    };

    if (type === "field-rule-move" || type === "field-rule-resize") {
      if (index === undefined || !fieldRulePairs[index]) return;
      setDragState({
        ...baseState,
        initialPair: JSON.parse(JSON.stringify(fieldRulePairs[index])),
      });
    } else {
      if (!tableOverlay) return;
      setDragState({
        ...baseState,
        initialOverlay: JSON.parse(JSON.stringify(tableOverlay)),
      });
    }
  };

  useEffect(() => {
    if (!dragState || !dims) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = imgRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = (e.clientX - rect.left) * (dims.natural.width / rect.width);
      const mouseY = (e.clientY - rect.top) * (dims.natural.height / rect.height);

      const dx = mouseX - dragState.startX;
      const dy = mouseY - dragState.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      const imgMaxWidth = dims.natural.width;
      const imgMaxHeight = dims.natural.height;

      if (dragState.type === "move" && dragState.initialOverlay) {
        const next = { ...dragState.initialOverlay };
        next.x = Math.max(0, Math.min(imgMaxWidth - next.width, next.x + dx));
        next.y = Math.max(0, Math.min(imgMaxHeight - next.height, next.y + dy));
        onTableOverlayChange?.(next);
        setDragState(prev => prev ? { ...prev, startX: mouseX, startY: mouseY, initialOverlay: next, totalDist: prev.totalDist + dist } : null);
      } else if (dragState.type === "resize-row" && dragState.index !== undefined && dragState.initialOverlay) {
        const next = { ...dragState.initialOverlay };
        next.rowHeights = [...next.rowHeights];
        const i = dragState.index;

        const currentH = next.rowHeights[i];
        const nextRowH = next.rowHeights[i + 1];

        // Local resize: adjust this row and next row. Total height stays constant.
        const safeDy = dy > 0
          ? Math.min(dy, nextRowH - 10)
          : Math.max(dy, 10 - currentH);

        next.rowHeights[i] += safeDy;
        next.rowHeights[i + 1] -= safeDy;

        onTableOverlayChange?.(next);
        setDragState(prev => prev ? { ...prev, startX: mouseX, startY: mouseY, initialOverlay: next, totalDist: prev.totalDist + dist } : null);
      } else if (dragState.type === "resize-col" && dragState.index !== undefined && dragState.initialOverlay) {
        const next = { ...dragState.initialOverlay };
        next.colWidths = [...next.colWidths];
        const i = dragState.index;

        const currentW = next.colWidths[i];
        const nextColW = next.colWidths[i + 1];

        // Local resize: adjust this column and next column. Total width stays constant.
        const safeDx = dx > 0
          ? Math.min(dx, nextColW - 10)
          : Math.max(dx, 10 - currentW);

        next.colWidths[i] += safeDx;
        next.colWidths[i + 1] -= safeDx;

        onTableOverlayChange?.(next);
        setDragState(prev => prev ? { ...prev, startX: mouseX, startY: mouseY, initialOverlay: next, totalDist: prev.totalDist + dist } : null);
      } else if (dragState.type === "resize-corner" && dragState.corner && dragState.initialOverlay) {
        const next = { ...dragState.initialOverlay };
        const { corner } = dragState;
        if (corner === "bottom-right") {
          const nextW = Math.max(50, next.width + dx);
          const nextH = Math.max(50, next.height + dy);
          const scaleX = nextW / next.width;
          const scaleY = nextH / next.height;
          next.width = nextW;
          next.height = nextH;
          next.colWidths = next.colWidths.map(w => w * scaleX);
          next.rowHeights = next.rowHeights.map(h => h * scaleY);
        } else if (corner === "bottom-left") {
          const nextW = Math.max(50, next.width - dx);
          const nextH = Math.max(50, next.height + dy);
          const actualDx = nextW - next.width;
          const scaleX = nextW / next.width;
          const scaleY = nextH / next.height;
          next.x -= actualDx;
          next.width = nextW;
          next.height = nextH;
          next.colWidths = next.colWidths.map(w => w * scaleX);
          next.rowHeights = next.rowHeights.map(h => h * scaleY);
        } else if (corner === "top-right") {
          const nextW = Math.max(50, next.width + dx);
          const nextH = Math.max(50, next.height - dy);
          const actualDy = nextH - next.height;
          const scaleX = nextW / next.width;
          const scaleY = nextH / next.height;
          next.y -= actualDy;
          next.width = nextW;
          next.height = nextH;
          next.colWidths = next.colWidths.map(w => w * scaleX);
          next.rowHeights = next.rowHeights.map(h => h * scaleY);
        } else if (corner === "top-left") {
          const nextW = Math.max(50, next.width - dx);
          const nextH = Math.max(50, next.height - dy);
          const actualDx = nextW - next.width;
          const actualDy = nextH - next.height;
          const scaleX = nextW / next.width;
          const scaleY = nextH / next.height;
          next.x -= actualDx;
          next.y -= actualDy;
          next.width = nextW;
          next.height = nextH;
          next.colWidths = next.colWidths.map(w => w * scaleX);
          next.rowHeights = next.rowHeights.map(h => h * scaleY);
        }
        onTableOverlayChange?.(next);
        setDragState(prev => prev ? { ...prev, startX: mouseX, startY: mouseY, initialOverlay: next, totalDist: prev.totalDist + dist } : null);
      } else if (dragState.type === "field-rule-move" && dragState.index !== undefined && dragState.initialPair) {
        const pair = JSON.parse(JSON.stringify(dragState.initialPair));
        const boxType = dragState.subType || "whole";

        if (boxType === "whole") {
          const nextX = Math.max(0, Math.min(imgMaxWidth - pair.whole.w, pair.whole.x + dx));
          const nextY = Math.max(0, Math.min(imgMaxHeight - pair.whole.h, pair.whole.y + dy));
          const actualDx = nextX - pair.whole.x;
          const actualDy = nextY - pair.whole.y;

          pair.whole.x = nextX;
          pair.whole.y = nextY;
          if (pair.key) { pair.key.x += actualDx; pair.key.y += actualDy; }
          if (pair.value) { pair.value.x += actualDx; pair.value.y += actualDy; }
        } else if (boxType === "key" || boxType === "value") {
          const box = pair[boxType];
          if (box) {
            box.x = Math.max(0, Math.min(imgMaxWidth - box.w, box.x + dx));
            box.y = Math.max(0, Math.min(imgMaxHeight - box.h, box.y + dy));

            // Re-calc whole box
            const boxes = [pair.key, pair.value].filter(b => b && b.w > 0) as { x: number, y: number, w: number, h: number }[];
            if (boxes.length > 0) {
              const xmin = Math.min(...boxes.map(b => b.x));
              const ymin = Math.min(...boxes.map(b => b.y));
              const xmax = Math.max(...boxes.map(b => b.x + b.w));
              const ymax = Math.max(...boxes.map(b => b.y + b.h));
              pair.whole = { x: xmin, y: ymin, w: xmax - xmin, h: ymax - ymin };
            }
          }
        }
        onFieldRuleChange?.(dragState.index, pair);
        setDragState(prev => prev ? { ...prev, startX: mouseX, startY: mouseY, initialPair: pair, totalDist: prev.totalDist + dist } : null);
      } else if (dragState.type === "field-rule-resize" && dragState.index !== undefined && dragState.initialPair) {
        const pair = JSON.parse(JSON.stringify(dragState.initialPair)); // Deep clone
        const boxType = dragState.subType || "whole";
        const box = boxType === "whole" ? pair.whole : pair[boxType];

        if (box) {
          const clampedMouseX = Math.max(0, Math.min(imgMaxWidth, mouseX));
          const clampedMouseY = Math.max(0, Math.min(imgMaxHeight, mouseY));

          if (dragState.corner === "bottom-right") {
            box.w = Math.max(10, clampedMouseX - box.x);
            box.h = Math.max(10, clampedMouseY - box.y);
          } else if (dragState.corner === "top-left") {
            const clampedX = Math.max(0, Math.min(box.x + box.w - 10, clampedMouseX));
            const clampedY = Math.max(0, Math.min(box.y + box.h - 10, clampedMouseY));
            box.w += box.x - clampedX;
            box.x = clampedX;
            box.h += box.y - clampedY;
            box.y = clampedY;
          } else if (dragState.corner === "top-right") {
            const clampedY = Math.max(0, Math.min(box.y + box.h - 10, clampedMouseY));
            box.h += box.y - clampedY;
            box.y = clampedY;
            box.w = Math.max(10, clampedMouseX - box.x);
          } else if (dragState.corner === "bottom-left") {
            const clampedX = Math.max(0, Math.min(box.x + box.w - 10, clampedMouseX));
            box.w += box.x - clampedX;
            box.x = clampedX;
            box.h = Math.max(10, clampedMouseY - box.y);
          }

          if (boxType === "whole") {
            // Constrain whole box so it never shrinks smaller than its key/value bounds
            const innerBoxes = [pair.key, pair.value].filter(b => b && b.w > 0) as { x: number, y: number, w: number, h: number }[];
            if (innerBoxes.length > 0) {
              const innerXMin = Math.min(...innerBoxes.map(b => b.x));
              const innerYMin = Math.min(...innerBoxes.map(b => b.y));
              const innerXMax = Math.max(...innerBoxes.map(b => b.x + b.w));
              const innerYMax = Math.max(...innerBoxes.map(b => b.y + b.h));

              if (innerXMax > box.x + box.w) box.w = innerXMax - box.x;
              if (innerYMax > box.y + box.h) box.h = innerYMax - box.y;
              if (innerXMin < box.x) {
                box.w += box.x - innerXMin;
                box.x = innerXMin;
              }
              if (innerYMin < box.y) {
                box.h += box.y - innerYMin;
                box.y = innerYMin;
              }
            }
          } else {
            // Only recalculate whole when BOTH key and value exist (matches old code)
            if (pair.value && pair.value.w > 0 && pair.key && pair.key.w > 0) {
              const boxes = [pair.key, pair.value] as { x: number, y: number, w: number, h: number }[];
              const xmin = Math.min(...boxes.map(b => b.x));
              const ymin = Math.min(...boxes.map(b => b.y));
              const xmax = Math.max(...boxes.map(b => b.x + b.w));
              const ymax = Math.max(...boxes.map(b => b.y + b.h));
              pair.whole = { x: xmin, y: ymin, w: xmax - xmin, h: ymax - ymin };
            } else if (pair.value && pair.value.w > 0) {
              pair.whole = { ...pair.value };
            }
            // If no value, whole box stays unchanged
          }
        }

        onFieldRuleChange?.(dragState.index, pair);
        // Do NOT update startX/startY or initialPair here, because mouseX/Y is absolute against the initialPair.
        setDragState(prev => prev ? { ...prev, totalDist: prev.totalDist + dist } : null);
      }
    };

    const handleMouseUp = () => {
      if (dragState && dragState.totalDist < 5 && (dragState.type === "field-rule-move" || dragState.type === "field-rule-resize")) {
        if (dragState.index !== undefined) {
          onFieldRuleSelect?.(dragState.index);
        }
      }
      setDragState(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragState, dims, zoom]);

  // Recalculate rendered size on load and when zoom changes.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    onImageRef?.(img);
    const update = () => {
      const naturalW = img.naturalWidth || parseHint(naturalSizeHint)?.[0] || 0;
      const naturalH = img.naturalHeight || parseHint(naturalSizeHint)?.[1] || 0;
      if (naturalW && naturalH) {
        onImageDimensions?.({ width: naturalW, height: naturalH });
      }
      setDims({
        natural: { width: naturalW, height: naturalH },
        rendered: { width: img.clientWidth, height: img.clientHeight },
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(img);
    return () => {
      ro.disconnect();
      onImageRef?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naturalSizeHint, zoom, src, onImageDimensions]);

  // Auto-scroll: when the active highlight moves off-screen, bring it
  // into view on the closest scrollable ancestor.
  useEffect(() => {
    if (!autoScrollToHighlight) return;
    const img = imgRef.current;
    const box = parseBoundary(highlightBoundary);
    if (!img || !box || !dims) return;
    const naturalW = dims.natural.width || 1;
    const naturalH = dims.natural.height || 1;
    const imgRect = img.getBoundingClientRect();
    const scaleX = imgRect.width / naturalW;
    const scaleY = imgRect.height / naturalH;
    const cx = imgRect.left + (box.x + box.width / 2) * scaleX;
    const cy = imgRect.top + (box.y + box.height / 2) * scaleY;

    const inViewX = cx >= 0 && cx <= window.innerWidth;
    const inViewY = cy >= 0 && cy <= window.innerHeight;
    if (inViewX && inViewY) return;

    img.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
  }, [highlightBoundary, dims, autoScrollToHighlight]);

  let box = parseBoundary(highlightBoundary);
  if (activeFieldPairIndex != null && fieldRulePairs[activeFieldPairIndex]) {
    const p = fieldRulePairs[activeFieldPairIndex].whole;
    box = { x: p.x, y: p.y, width: p.w, height: p.h };
  }

  const showOverlay =
    box &&
    dims &&
    dims.natural.width > 0 &&
    dims.natural.height > 0 &&
    dims.rendered.width > 0 &&
    dims.rendered.height > 0;

  return (
    <div
      // Zoom is implemented by setting the wrapper's WIDTH (in fitWidth
      // mode) or via a CSS scale transform anchored at the top-left.
      //
      // Why not just `transform: scale()` for both modes? Because CSS
      // transforms scale VISUALLY but don't change the element's layout
      // box. Inside an `overflow-auto` scroll container, that produces
      // two surprises at higher zoom levels:
      //   - `scrollWidth` still reflects the unscaled width, so the
      //     right edge isn't reachable in some browsers.
      //   - With the original `origin-top` (top-centre), the image grows
      //     equally left AND right of its centre. `scrollLeft: 0` shows
      //     the unscaled left edge, and the additional bulk on the left
      //     is unreachable (negative scroll is impossible).
      //
      // In fitWidth mode (Final Checker, Verification Cockpit) the
      // image is already constrained to the parent's width, so we can
      // just set width to `${zoom}%`. The image (`w-full`) and overlay
      // SVGs (`w-full h-full`) follow naturally and the scroll container
      // sees correct overflow on every axis. In non-fitWidth mode the
      // wrapper is `inline-block` around an image with `max-w-3xl`, so
      // we keep the transform approach but anchor it at `top left` to
      // avoid the negative-scroll-left issue.
      className={`relative transition-all duration-150 ${fitWidth ? "block" : "inline-block origin-top-left"
        }`}
      style={
        fitWidth
          ? { width: `${zoom}%` }
          : {
            transform: `scale(${zoom / 100})`,
            transformOrigin: "top left",
          }
      }
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onMouseDown={handleImageMouseDown}
        onLoad={() => {
          const img = imgRef.current;
          if (!img) return;
          setDims({
            natural: {
              width: img.naturalWidth,
              height: img.naturalHeight,
            },
            rendered: {
              width: img.clientWidth,
              height: img.clientHeight,
            },
          });
        }}
        className={`block rounded-xl border border-gray-200 bg-white shadow-md ${fitWidth ? "h-auto w-full" : "max-w-3xl"
          }`}
      />

      {/* Field Rule Drawing & Overlays */}
      {dims && (
        <svg
          className="pointer-events-none absolute left-0 top-0 h-full w-full"
          viewBox={`0 0 ${dims.natural.width} ${dims.natural.height}`}
          preserveAspectRatio="none"
          style={{ zIndex: 10 }}
        >
          {/* Active Drawing Box */}
          {drawingSelection?.start && drawingSelection?.end && (
            <rect
              x={Math.min(drawingSelection.start.x, drawingSelection.end.x)}
              y={Math.min(drawingSelection.start.y, drawingSelection.end.y)}
              width={Math.abs(drawingSelection.end.x - drawingSelection.start.x)}
              height={Math.abs(drawingSelection.end.y - drawingSelection.start.y)}
              fill="rgba(59, 130, 246, 0.1)"
              stroke={pendingFieldPair ? "#2563eb" : "#3b82f6"} // Blue if drawing value, light blue if key
              strokeWidth={Math.max(2, dims.natural.width * 0.0015)}
              strokeDasharray="4 2"
            />
          )}

          {/* Pending Key (when drawing value in separate mode) */}
          {pendingFieldPair?.key && (
            <rect
              x={pendingFieldPair.key.x}
              y={pendingFieldPair.key.y}
              width={pendingFieldPair.key.w}
              height={pendingFieldPair.key.h}
              fill="rgba(239, 68, 68, 0.1)"
              stroke="#ef4444"
              strokeWidth={Math.max(2, dims.natural.width * 0.0015)}
            />
          )}

          {/* OCR Words Selection Layer */}
          {onWordClick && ocrWords && ocrWords.length > 0 && (
            <g className="ocr-words-layer opacity-0 hover:opacity-20 transition-opacity">
              {ocrWords.map((word, idx) => (
                <rect
                  key={idx}
                  x={word.x}
                  y={word.y}
                  width={word.w}
                  height={word.h}
                  fill="#3b82f6"
                  className="cursor-pointer"
                  style={{ pointerEvents: "all" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onWordClick(word.text);
                  }}
                >
                  <title>{word.text}</title>
                </rect>
              ))}
            </g>
          )}

          {/* Existing Field Rule Pairs */}
          {fieldRulePairs.length > 0 && fieldRulePairs.map((pair, idx) => {
            const isActive = activeFieldPairIndex === idx;
            const strokeScale = dims.natural.width * 0.001;

            return (
              <g key={`pair-${idx}`}>
                {/* Pair Number Badge — small grey/blue numbered circle
                    next to the field-name label. Suppressed whenever
                    the OCR `markers` layer below this block is active
                    because that layer already paints a prominent
                    numbered circle for the same field index, and
                    rendering both produces "two numbers per field". */}
                {(!markers || markers.length === 0) && (
                  <g transform={`translate(${pair.whole.x - 15}, ${pair.whole.y - 15})`}>
                    <circle
                      cx={0}
                      cy={0}
                      r={Math.max(8, strokeScale * 5)}
                      fill={isActive ? "#007bff" : "#6c757d"}
                      stroke="white"
                      strokeWidth={1.5}
                      data-interaction="true"
                      style={{ pointerEvents: "all", cursor: "pointer" }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        onFieldRuleSelect?.(idx);
                      }}
                    />
                    <text
                      x={0}
                      y={0}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="white"
                      fontSize={Math.max(10, strokeScale * 6)}
                      fontWeight="bold"
                      style={{ pointerEvents: "none" }}
                    >
                      {idx + 1}
                    </text>
                  </g>
                )}


                {/* Field Name Label - Always Visible */}
                <g transform={`translate(${pair.whole.x}, ${pair.whole.y})`} style={{ pointerEvents: "all" }}>
                  <rect
                    x={0}
                    y={-22 / (zoom / 100)}
                    width={Math.max(60, (pair.field.length * 7 + 10) / (zoom / 100))}
                    height={20 / (zoom / 100)}
                    fill={isActive ? "#007bff" : "#6c757d"}
                    rx={2}
                    style={{ cursor: "pointer" }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      onFieldRuleSelect?.(idx);
                    }}
                  />
                  <text
                    x={5 / (zoom / 100)}
                    y={-7 / (zoom / 100)}
                    fill="white"
                    fontSize={12 / (zoom / 100)}
                    fontWeight="bold"
                    style={{ pointerEvents: "none" }}
                  >
                    {pair.field || "Select Field..."}
                  </text>
                </g>

                {/* Whole Box rect — rendered FIRST so the value/key
                    boxes paint on top and capture clicks for their own
                    areas. Whole-box body catches clicks on the
                    "fringe" — the part of the bounding rectangle that
                    isn't covered by either inner box (e.g. the blue
                    half on the right when only a key is drawn). The
                    whole-box resize handles are rendered later so they
                    sit on top of everything and stay clickable. */}
                <rect
                  x={pair.whole.x}
                  y={pair.whole.y}
                  width={pair.whole.w}
                  height={pair.whole.h}
                  fill="rgba(0, 123, 255, 0.08)"
                  stroke="#007bff"
                  strokeWidth={isActive ? 3 : 2}
                  data-interaction="true"
                  style={{ pointerEvents: "all", cursor: "move" }}
                  onMouseDown={(e) => handleMouseDown(e, "field-rule-move", idx, undefined, "whole")}
                />

                {/* Value Box (Green) - Rendered before key. Box BODY
                    is interactive (clicking anywhere selects the pair
                    / opens the field dropdown); resize handle circles
                    are rendered after so SVG paint order lets them win
                    for handle clicks. */}
                {pair.value && pair.value.w > 0 && (
                  <g>
                    <rect
                      x={pair.value.x}
                      y={pair.value.y}
                      width={pair.value.w}
                      height={pair.value.h}
                      fill="rgba(40, 167, 69, 0.15)"
                      stroke="#28a745"
                      strokeWidth={isActive ? 2 : 1}
                      data-interaction="true"
                      style={{ pointerEvents: "all", cursor: "move" }}
                      onMouseDown={(e) => handleMouseDown(e, "field-rule-move", idx, undefined, "value")}
                    />
                    {isActive && (
                      <>
                        <circle
                          cx={pair.value.x}
                          cy={pair.value.y}
                          r={Math.max(6, strokeScale * 4)}
                          fill="white"
                          stroke="#28a745"
                          strokeWidth={1.5}
                          data-interaction="true"
                          style={{ pointerEvents: "all", cursor: "nw-resize" }}
                          onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "top-left", "value")}
                        />
                        <circle
                          cx={pair.value.x + pair.value.w}
                          cy={pair.value.y + pair.value.h}
                          r={Math.max(6, strokeScale * 4)}
                          fill="white"
                          stroke="#28a745"
                          strokeWidth={1.5}
                          data-interaction="true"
                          style={{ pointerEvents: "all", cursor: "se-resize" }}
                          onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "bottom-right", "value")}
                        />
                        <circle
                          cx={pair.value.x + pair.value.w}
                          cy={pair.value.y}
                          r={Math.max(6, strokeScale * 4)}
                          fill="white"
                          stroke="#28a745"
                          strokeWidth={1.5}
                          data-interaction="true"
                          style={{ pointerEvents: "all", cursor: "ne-resize" }}
                          onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "top-right", "value")}
                        />
                        <circle
                          cx={pair.value.x}
                          cy={pair.value.y + pair.value.h}
                          r={Math.max(6, strokeScale * 4)}
                          fill="white"
                          stroke="#28a745"
                          strokeWidth={1.5}
                          data-interaction="true"
                          style={{ pointerEvents: "all", cursor: "sw-resize" }}
                          onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "bottom-left", "value")}
                        />
                      </>
                    )}
                  </g>
                )}

                {/* Key Box (Red) - Rendered LAST so its circles are
                    always on top. Box BODY is interactive — same
                    rationale as the value box above. */}
                {pair.key && pair.key.w > 0 && (
                  <g>
                    <rect
                      x={pair.key.x}
                      y={pair.key.y}
                      width={pair.key.w}
                      height={pair.key.h}
                      fill="rgba(220, 53, 69, 0.2)"
                      stroke="#dc3545"
                      strokeWidth={isActive ? 2.5 : 1.5}
                      data-interaction="true"
                      style={{ pointerEvents: "all", cursor: "move" }}
                      onMouseDown={(e) => handleMouseDown(e, "field-rule-move", idx, undefined, "key")}
                    />
                    {isActive && (
                      <>
                        {/* Top Left */}
                        <circle
                          cx={pair.key.x}
                          cy={pair.key.y}
                          r={Math.max(6, strokeScale * 4)}
                          fill="white"
                          stroke="#dc3545"
                          strokeWidth={1.5}
                          data-interaction="true"
                          style={{ pointerEvents: "all", cursor: "nw-resize" }}
                          onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "top-left", "key")}
                        />
                        {/* Bottom Right */}
                        <circle
                          cx={pair.key.x + pair.key.w}
                          cy={pair.key.y + pair.key.h}
                          r={Math.max(6, strokeScale * 4)}
                          fill="white"
                          stroke="#dc3545"
                          strokeWidth={1.5}
                          data-interaction="true"
                          style={{ pointerEvents: "all", cursor: "se-resize" }}
                          onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "bottom-right", "key")}
                        />
                        {/* Top Right */}
                        <circle
                          cx={pair.key.x + pair.key.w}
                          cy={pair.key.y}
                          r={Math.max(6, strokeScale * 4)}
                          fill="white"
                          stroke="#dc3545"
                          strokeWidth={1.5}
                          data-interaction="true"
                          style={{ pointerEvents: "all", cursor: "ne-resize" }}
                          onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "top-right", "key")}
                        />
                        {/* Bottom Left */}
                        <circle
                          cx={pair.key.x}
                          cy={pair.key.y + pair.key.h}
                          r={Math.max(6, strokeScale * 4)}
                          fill="white"
                          stroke="#dc3545"
                          strokeWidth={1.5}
                          data-interaction="true"
                          style={{ pointerEvents: "all", cursor: "sw-resize" }}
                          onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "bottom-left", "key")}
                        />
                      </>
                    )}
                  </g>
                )}

                {/* Whole Box Resize Handles - Only when Active.
                    These render LAST so they sit on top of every
                    rect (including the inner key/value boxes) and
                    stay clickable for resize. */}
                {isActive && (
                  <>
                    <circle
                      cx={pair.whole.x}
                      cy={pair.whole.y}
                      r={Math.max(6, strokeScale * 4)}
                      fill="white"
                      stroke="#007bff"
                      strokeWidth={1.5}
                      data-interaction="true"
                      style={{ pointerEvents: "all", cursor: "nw-resize" }}
                      onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "top-left", "whole")}
                    />
                    <circle
                      cx={pair.whole.x + pair.whole.w}
                      cy={pair.whole.y + pair.whole.h}
                      r={Math.max(6, strokeScale * 4)}
                      fill="white"
                      stroke="#007bff"
                      strokeWidth={1.5}
                      data-interaction="true"
                      style={{ pointerEvents: "all", cursor: "se-resize" }}
                      onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "bottom-right", "whole")}
                    />
                    <circle
                      cx={pair.whole.x + pair.whole.w}
                      cy={pair.whole.y}
                      r={Math.max(6, strokeScale * 4)}
                      fill="white"
                      stroke="#007bff"
                      strokeWidth={1.5}
                      data-interaction="true"
                      style={{ pointerEvents: "all", cursor: "ne-resize" }}
                      onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "top-right", "whole")}
                    />
                    <circle
                      cx={pair.whole.x}
                      cy={pair.whole.y + pair.whole.h}
                      r={Math.max(6, strokeScale * 4)}
                      fill="white"
                      stroke="#007bff"
                      strokeWidth={1.5}
                      data-interaction="true"
                      style={{ pointerEvents: "all", cursor: "sw-resize" }}
                      onMouseDown={(e) => handleMouseDown(e, "field-rule-resize", idx, "bottom-left", "whole")}
                    />
                  </>
                )}

                {/* Number Badge (removed) — this was a second, larger
                    numbered circle drawn at approximately the same
                    location as the "Pair Number Badge" above (line
                    ~699). They rendered identical indices on top of
                    each other, which read as "every field has two
                    numbers" on the cockpit. The smaller pair badge is
                    the one we keep because it visually anchors the
                    sibling field-name label. If you ever need a larger
                    standalone marker again, re-add a single block
                    here; do not re-introduce both. */}
              </g>
            );
          })}
        </svg>
      )}

      {/* Numbered field markers, drawn beneath the active highlight.
          `pointer-events-none` on the SVG itself so the rule-mode
          drawing handler on the underlying <img> still receives the
          mousedown. Individual <g> markers re-enable pointer events
          via the inline style below only when an onMarkerClick handler
          is provided.

          These are the prominent green/indigo circles users navigate
          by — we keep them rendering at all times when present. To
          avoid the "every field shows two numbers" problem, the smaller
          grey "Pair Number Badge" inside the fieldRulePairs loop above
          is suppressed when this marker layer is active. See the badge
          gate (`markers.length === 0`) at that block for the other
          half of this pairing. */}
      {!cleanView && markers && markers.length > 0 && dims ? (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox={`0 0 ${dims.natural.width} ${dims.natural.height}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {markers.map((m) => {
            const isActive = activeMarkerFieldName === m.fieldName;
            const cx = m.keyBbox.xmin - 14;
            const cy = m.keyBbox.ymin + 10;
            const r = Math.max(14, dims.natural.width * 0.011);
            return (
              <g
                key={`${m.fieldName}-${m.index}`}
                onClick={() => onMarkerClick?.(m)}
                className={onMarkerClick ? "cursor-pointer" : ""}
                style={{ pointerEvents: onMarkerClick ? "auto" : "none" }}
              >
                <circle cx={cx} cy={cy} r={r + 2} fill="white" opacity={0.9} />
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={isActive ? "rgb(99, 102, 241)" : "rgb(16, 185, 129)"}
                  stroke="white"
                  strokeWidth={2}
                />
                <text
                  x={cx}
                  y={cy}
                  fill="white"
                  fontSize={r * 1.25}
                  fontWeight={700}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontFamily="ui-sans-serif, system-ui, -apple-system, sans-serif"
                >
                  {m.index}
                </text>
              </g>
            );
          })}
        </svg>
      ) : null}

      {/* Red Table Overlay */}
      {!cleanView && tableOverlay && dims ? (
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox={`0 0 ${dims.natural.width} ${dims.natural.height}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          style={{ pointerEvents: "auto" }}
        >
          {/* Main area - for moving */}
          <rect
            x={tableOverlay.x}
            y={tableOverlay.y}
            width={tableOverlay.width}
            height={tableOverlay.height}
            fill="rgba(220, 38, 38, 0.05)"
            stroke="rgba(220, 38, 38, 0.9)"
            strokeWidth={Math.max(5, dims.natural.width * 0.004)}
            className="cursor-move"
            onMouseDown={(e) => handleMouseDown(e, "move")}
            onClick={(e) => { e.stopPropagation(); onTableClick?.(); }}
            style={{ pointerEvents: "all" }}
          />

          {/* Header highlight (first row) */}
          {tableOverlay.rowHeights.length > 0 && (
            <rect
              x={tableOverlay.x}
              y={tableOverlay.y}
              width={tableOverlay.width}
              height={tableOverlay.rowHeights[0]}
              fill="rgba(220, 38, 38, 0.15)"
              stroke="none"
              style={{ pointerEvents: "none" }}
            />
          )}

          {/* Vertical lines - for resizing columns */}
          {(() => {
            let currentX = tableOverlay.x;
            return tableOverlay.colWidths.slice(0, -1).map((w, i) => {
              currentX += w;
              const colIdx = i;
              return (
                <g key={`v-group-${i}`}>
                  <line
                    x1={currentX}
                    y1={tableOverlay.y}
                    x2={currentX}
                    y2={tableOverlay.y + tableOverlay.height}
                    stroke="rgba(220, 38, 38, 0.8)"
                    strokeWidth={Math.max(4, dims.natural.width * 0.003)}
                  />
                  {/* Invisible hit area */}
                  <rect
                    x={currentX - 10}
                    y={tableOverlay.y}
                    width={20}
                    height={tableOverlay.height}
                    fill="transparent"
                    className="cursor-col-resize"
                    onMouseDown={(e) => handleMouseDown(e, "resize-col", i)}
                    style={{ pointerEvents: "all" }}
                  />
                </g>
              );
            });
          })()}

          {/* Header Cells - for mapping */}
          {tableOverlay.rowHeights.length > 0 && (() => {
            let currentX = tableOverlay.x;
            return tableOverlay.colWidths.map((w, colIdx) => {
              const mapping = headerMappings[`0_${colIdx}`];
              const isActive = activeHeaderIndex?.rowIndex === 0 && activeHeaderIndex?.colIndex === colIdx;
              const x = currentX;
              currentX += w;

              return (
                <g key={`header-cell-${colIdx}`}>
                  {/* Highlight if mapped or active */}
                  {(mapping || isActive) && (
                    <rect
                      x={x}
                      y={tableOverlay.y}
                      width={w}
                      height={tableOverlay.rowHeights[0]}
                      fill={isActive ? "rgba(59, 130, 246, 0.2)" : "rgba(255, 255, 0, 0.3)"}
                      stroke="none"
                    />
                  )}
                  {/* Click area */}
                  <rect
                    x={x}
                    y={tableOverlay.y}
                    width={w}
                    height={tableOverlay.rowHeights[0]}
                    fill="transparent"
                    className="cursor-pointer"
                    onClick={(e) => { e.stopPropagation(); onHeaderClick?.(0, colIdx); onTableClick?.(); }}
                    style={{ pointerEvents: "all" }}
                  />
                  {/* Label above */}
                  {mapping && (
                    <g>
                      <rect
                        x={x}
                        y={tableOverlay.y - 24}
                        width={w}
                        height={24}
                        fill="#f1f5f9"
                        stroke="rgba(220, 38, 38, 0.5)"
                        strokeWidth={1}
                      />
                      <text
                        x={x + 4}
                        y={tableOverlay.y - 12}
                        fill="#1e293b"
                        fontSize={12}
                        fontWeight={600}
                        dominantBaseline="middle"
                        fontFamily="ui-sans-serif, system-ui, sans-serif"
                      >
                        {mapping.length > (w / 8) ? mapping.substring(0, Math.floor(w / 8)) + "..." : mapping}
                      </text>
                    </g>
                  )}
                </g>
              );
            });
          })()}

          {/* Horizontal lines - for resizing rows */}
          {(() => {
            let currentY = tableOverlay.y;
            return tableOverlay.rowHeights.slice(0, -1).map((h, i) => {
              currentY += h;
              return (
                <g key={`h-group-${i}`}>
                  <line
                    x1={tableOverlay.x}
                    y1={currentY}
                    x2={tableOverlay.x + tableOverlay.width}
                    y2={currentY}
                    stroke="rgba(220, 38, 38, 0.8)"
                    strokeWidth={Math.max(4, dims.natural.width * 0.003)}
                  />
                  {/* Invisible hit area */}
                  <rect
                    x={tableOverlay.x}
                    y={currentY - 10}
                    width={tableOverlay.width}
                    height={20}
                    fill="transparent"
                    className="cursor-row-resize"
                    onMouseDown={(e) => handleMouseDown(e, "resize-row", i)}
                    style={{ pointerEvents: "all" }}
                  />
                </g>
              );
            });
          })()}

          {/* Corner Handles - for resizing the whole table */}
          {(() => {
            const r = Math.max(12, dims.natural.width * 0.008);
            const corners: Array<"top-left" | "top-right" | "bottom-left" | "bottom-right"> = [
              "top-left",
              "top-right",
              "bottom-left",
              "bottom-right",
            ];
            return corners.map((c) => {
              let cx = tableOverlay.x;
              let cy = tableOverlay.y;
              if (c === "top-right") cx += tableOverlay.width;
              if (c === "bottom-left") cy += tableOverlay.height;
              if (c === "bottom-right") {
                cx += tableOverlay.width;
                cy += tableOverlay.height;
              }

              return (
                <circle
                  key={`corner-${c}`}
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill="white"
                  stroke="rgba(220, 38, 38, 0.8)"
                  strokeWidth={Math.max(2, dims.natural.width * 0.0012)}
                  className={`pointer-events-all ${c === "top-left" || c === "bottom-right" ? "cursor-nwse-resize" : "cursor-nesw-resize"}`}
                  onMouseDown={(e) => handleMouseDown(e, "resize-corner", undefined, c)}
                />
              );
            });
          })()}
        </svg>
      ) : null}

      {!cleanView && tableOverlay && dims && renderTableOverlay
        ? renderTableOverlay(tableOverlay, activeHeaderIndex || null, dims.natural)
        : null}

      {!cleanView && showOverlay ? (
        <>
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full"
            viewBox={`0 0 ${dims.natural.width} ${dims.natural.height}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {/* Soft glow underlay so the bbox is visible on busy documents */}
            <rect
              x={box!.x - 4}
              y={box!.y - 4}
              width={box!.width + 8}
              height={box!.height + 8}
              fill="rgba(99, 102, 241, 0.08)"
              rx={6}
            />
            <rect
              id="active-bbox-rect"
              data-active-bbox="true"
              x={box!.x}
              y={box!.y}
              width={box!.width}
              height={box!.height}
              fill="rgba(99, 102, 241, 0.10)"
              stroke="rgba(99, 102, 241, 1)"
              strokeWidth={Math.max(3, dims.natural.width * 0.0022)}
              rx={4}
            />
          </svg>
          {renderOverlay ? renderOverlay(box!, dims.natural) : null}
        </>
      ) : null}
    </div>
  );
}

function parseBoundary(b?: string): {
  x: number;
  y: number;
  width: number;
  height: number;
} | null {
  if (!b) return null;
  const parts = b.split(",").map((p) => parseFloat(p.trim()));
  if (parts.length < 4 || parts.some((n) => Number.isNaN(n))) return null;
  return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
}

function parseHint(s?: string): [number, number] | null {
  if (!s) return null;
  const parts = s.split(",").map((p) => parseFloat(p.trim()));
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return null;
  return [parts[0], parts[1]];
}
